import { useCallback } from 'react'
import { useNavigate, type NavigateOptions } from 'react-router-dom'
import { cn } from '@/common/lib/cn/cn'
import { navigateOnClick } from '@/common/lib/navigateOnClick'
import './card.css'

export type CardTopBorderColor = 'purple' | 'blue' | 'green' | 'orange'

export type GenerateCardProps = {
    color?: 'primary' | 'transparent'
    className?: string
    cardBodyClassName?: string
    /** Горизонтальный скролл таблицы внутри card-body, без раздувания карточки */
    tableScroll?: boolean
    type?: 'product' | 'result-first' | 'result-last' | 'result' | 'transparent'
    topBorderColor?: CardTopBorderColor
    /** SPA-путь: клик навигирует, Ctrl/Cmd/middle — новая вкладка */
    to?: string
    navigateOptions?: NavigateOptions
} & React.HTMLAttributes<HTMLDivElement>

const buildCardClassName = ({
    color,
    type,
    topBorderColor,
    tableScroll,
    withNavigate,
    className,
}: {
    color: NonNullable<GenerateCardProps['color']>
    type: NonNullable<GenerateCardProps['type']>
    topBorderColor?: CardTopBorderColor
    tableScroll: boolean
    withNavigate: boolean
    className?: string
}) =>
    cn(
        'card',
        {
            'bg-pixorion-dark-gray': color === 'primary',
            'bg-transparent': color === 'transparent',
            'card-product': type === 'product',
            'card-product-result': type === 'result',
            'card-product-result card-first': type === 'result-first',
            'card-product-result card-last': type === 'result-last',
            'card-transparent': type === 'transparent',
            'card-top-border-purple': topBorderColor === 'purple',
            'card-top-border-blue': topBorderColor === 'blue',
            'card-top-border-green': topBorderColor === 'green',
            'card-top-border-orange': topBorderColor === 'orange',
        },
        tableScroll && 'min-w-0 w-full',
        withNavigate && 'cursor-pointer',
        className
    )

const CardWithNavigate = ({
    children,
    color = 'primary',
    className,
    cardBodyClassName,
    tableScroll = false,
    type = 'product',
    topBorderColor,
    to,
    navigateOptions,
    onClick,
    onKeyDown,
    role,
    tabIndex,
    ...props
}: GenerateCardProps & { to: string }) => {
    const navigate = useNavigate()

    const handleClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            navigateOnClick(e, to, navigate, navigateOptions)
            onClick?.(e)
        },
        [to, navigate, navigateOptions, onClick]
    )

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                navigate(to, navigateOptions)
            }
            onKeyDown?.(e)
        },
        [to, navigate, navigateOptions, onKeyDown]
    )

    return (
        <div
            className={buildCardClassName({
                color,
                type,
                topBorderColor,
                tableScroll,
                withNavigate: true,
                className,
            })}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            role={role ?? 'link'}
            tabIndex={tabIndex ?? 0}
            {...props}
        >
            <div className={cn('card-body', tableScroll && 'min-w-0 overflow-hidden', cardBodyClassName)}>
                {children}
            </div>
        </div>
    )
}

export const Card = ({ to, navigateOptions, ...props }: GenerateCardProps) => {
    if (to) {
        return <CardWithNavigate to={to} navigateOptions={navigateOptions} {...props} />
    }

    const {
        children,
        color = 'primary',
        className,
        cardBodyClassName,
        tableScroll = false,
        type = 'product',
        topBorderColor,
        ...rest
    } = props

    return (
        <div
            className={buildCardClassName({
                color,
                type,
                topBorderColor,
                tableScroll,
                withNavigate: false,
                className,
            })}
            {...rest}
        >
            <div className={cn('card-body', tableScroll && 'min-w-0 overflow-hidden', cardBodyClassName)}>
                {children}
            </div>
        </div>
    )
}
